import { Button } from '@components/Button/Button';
import { EditableNumberCell } from '@components/common/UTable/EditableNumberCell';
import { ITableActionProps, ITableColumn } from '@components/common/UTable/UTable';
import { Popconfirm, Space, Tooltip } from 'antd';
import _ from 'lodash';

import { Auth, hasPermission } from '@/shared/security/hasPermission';
import DateMapper, { DateTimeFormat } from '@/utils/dateUtils';

export const tableColumnsProcessed = (
  columns: ITableColumn[],
  auth: Auth,
  actions?: (record: any) => ITableActionProps[],
  width?: number,
) => {
  const formattedColumns = columns.map(tableColumnFormatted);

  return actions
    ? [
        ...formattedColumns,
        {
          key: 'actions',
          width: width,
          render: (_value: any, record: any) => (
            <Space size="small">
              {actions(record)
                .filter((action) => hasPermission(auth, action?.security))
                .map((action) =>
                  action?.popconfirm ? (
                    <Popconfirm
                      title={action?.popconfirm?.title}
                      description={action?.popconfirm?.description}
                      onConfirm={() => action.action(record)}
                      okText={action?.popconfirm?.okText}
                      cancelText={action?.popconfirm?.cancelText}>
                      <Button
                        type="text"
                        shape="circle"
                        icon={action.icon}
                        style={{ fontSize: 16 }}
                        disabled={action.disabled}
                      />
                    </Popconfirm>
                  ) : (
                    <Tooltip title={action.tooltip}>
                      <Button
                        type="text"
                        shape="circle"
                        icon={action.icon}
                        style={{ fontSize: 16 }}
                        onClick={() => action.action(record)}
                        disabled={action.disabled}
                      />
                    </Tooltip>
                  ),
                )}
            </Space>
          ),
          fixed: 'right' as const,
        },
      ]
    : formattedColumns;
};

const tableColumnFormatted = (column: ITableColumn): ITableColumn => {
  const baseRender = _.isFunction(column.render)
    ? column.render
    : (value: any) => {
        switch (column.type) {
          case 'date':
            return value
              ? DateMapper(value, DateTimeFormat.LOCAL_DATE).format(DateTimeFormat.DATE)
              : (column.payload?.defaultValue ?? value);
          case 'datetime':
            return value
              ? DateMapper(value, DateTimeFormat.LOCAL_DATE_TIME).format(
                  DateTimeFormat.DATE_TIME,
                )
              : (column.payload?.defaultValue ?? value);
          case 'select':
            if (_.isArray(value)) {
              return value
                ?.map(
                  (item: any) =>
                    column.payload?.options?.find(
                      (opt: any) =>
                        opt[column.payload?.fieldNames?.value ?? 'value'] === item,
                    )?.[column.payload?.fieldNames?.label ?? 'label'] ?? item,
                )
                .join(', \r\n');
            }
            return (
              column.payload?.options?.find(
                (opt: any) => opt[column.payload?.fieldNames?.value ?? 'value'] === value,
              )?.[column.payload?.fieldNames?.label ?? 'label'] ?? value
            );
          case 'text':
            return !_.isNil(value) && value !== 'null'
              ? value
              : (column.payload?.defaultValue ?? '');
          default:
            return !_.isNil(value) && value !== 'null'
              ? value
              : (column.payload?.defaultValue ?? '');
        }
      };

  const render =
    column.editable?.type === 'number'
      ? (value: any, record: any) => (
          <EditableNumberCell
            value={Number(value ?? 0)}
            record={record}
            config={column.editable!}
          />
        )
      : baseRender;

  return {
    ...column,
    key: column.dataIndex,
    dataIndex: column.dataIndex?.split('.'),
    render: render,
    children: column.children?.map(tableColumnFormatted),
    sorter: !!column.sorterKey || column?.sorter,
  };
};
